import type { User } from '@ifixit/auth-sdk';
import { type IFixitAPIClient, useIFixitApiClient } from '@ifixit/ifixit-api-client';
import { SentryError } from '@ifixit/sentry';
import {
   type CustomerAccessToken,
   type StorefrontClient,
   useShopifyStorefrontClient,
} from '@ifixit/shopify-storefront-client';
import { useCallback } from 'react';
import { getStoredCustomerAccessToken, updateCustomerAccessToken } from '../helpers/storage';

// Mirrors EXPIRY_SKEW in CustomerAccountApiClient.php
const EXPIRY_MARGIN_MS = 60 * 1000;

/**
 * Returns null when the store is on Customer Accounts and the user has no
 * usable token pair; the cart then attaches no buyer identity and shows retail
 * prices.
 */
export function useGetCustomerAccessToken() {
   const { client: storefrontClient, storeCode } = useShopifyStorefrontClient();
   const ifixitApiClient = useIFixitApiClient();
   return useCallback(
      async (user: User) => {
         const storedToken = getStoredCustomerAccessToken({ user, storeCode });
         if (storedToken && isStoredTokenUsable(storedToken)) {
            return storedToken;
         }
         const customerAccessToken = await createCustomerAccessToken({
            ifixitApiClient,
            storeCode,
            storefrontClient,
         });
         if (!customerAccessToken) {
            return null;
         }
         updateCustomerAccessToken({ customerAccessToken, storeCode, userId: user.id });
         return customerAccessToken;
      },
      [ifixitApiClient, storefrontClient, storeCode]
   );
}

export function isStoredTokenUsable(token: CustomerAccessToken): boolean {
   return new Date(token.expiresAt).getTime() - EXPIRY_MARGIN_MS > Date.now();
}

type CreateCustomerAccessTokenProps = {
   ifixitApiClient: IFixitAPIClient;
   storeCode: string;
   storefrontClient: StorefrontClient;
};
export async function createCustomerAccessToken({
   ifixitApiClient,
   storeCode,
   storefrontClient,
}: CreateCustomerAccessTokenProps): Promise<CustomerAccessToken | null> {
   const auth = await fetchShopifyAuthToken({ ifixitApiClient, storeCode });
   if (auth.type === 'multipass') {
      return exchangeMultipassToken({ multipassToken: auth.multipassToken, storefrontClient });
   }
   if (auth.accessToken === null) {
      return null;
   }
   return { accessToken: auth.accessToken, expiresAt: auth.expiresAt };
}

type ShopifyAuthTokenResponse =
   | { type: 'multipass'; multipassToken: string }
   | { type: 'customerAccount'; accessToken: string; expiresAt: string }
   | { type: 'customerAccount'; accessToken: null; authUrl: string };

type FetchShopifyAuthTokenProps = {
   ifixitApiClient: IFixitAPIClient;
   storeCode: string;
};
async function fetchShopifyAuthToken({
   ifixitApiClient,
   storeCode,
}: FetchShopifyAuthTokenProps): Promise<ShopifyAuthTokenResponse> {
   return ifixitApiClient.post<ShopifyAuthTokenResponse>(
      `store/user/shopify-auth-token?storeCode=${encodeURIComponent(storeCode)}`,
      'post-shopify-auth-token'
   );
}

type ExchangeMultipassTokenProps = {
   multipassToken: string;
   storefrontClient: StorefrontClient;
};
async function exchangeMultipassToken({
   multipassToken,
   storefrontClient,
}: ExchangeMultipassTokenProps): Promise<CustomerAccessToken> {
   const { customerAccessTokenCreateWithMultipass } =
      await storefrontClient.createCustomerAccessTokenWithMultipass({ multipassToken });
   if (customerAccessTokenCreateWithMultipass?.customerUserErrors?.length) {
      throw new SentryError('User errors when creating customer access token', {
         extra: { response: customerAccessTokenCreateWithMultipass },
      });
   }
   const customerAccessToken = customerAccessTokenCreateWithMultipass?.customerAccessToken;
   if (!customerAccessToken) {
      throw new SentryError('Failed to create customer access token', {
         extra: { response: customerAccessTokenCreateWithMultipass },
      });
   }
   return customerAccessToken;
}
